import React, { useState, useEffect } from 'react';

import { PageHeader } from 'components/layout/PageHeader';
import { supabase } from 'config/supabase';
import { useAuth } from 'contexts/AuthContext';

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  priceId: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const PRICE_ID = {
  STARTER: 'price_1SK29SS8iwJWuwSlQ4DtNGKU',
  GROWTH: 'price_1SKgIGS8iwJWuwSlxQTHMNOo',
  PRO: 'price_1SK2ApS8iwJWuwSlJKoIn8Mq',
};

const pricingPlans: PricingPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 299,
    priceId: PRICE_ID.STARTER,
    features: [
      '20 outputs/month',
      'Weekly group coaching',
      '<strong>Best for: Solo founders getting started</strong>',
    ],
    cta: 'Try Starter',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 999,
    priceId: PRICE_ID.GROWTH,
    features: [
      'Unlimited outputs',
      'Biweekly 1:1 coaching',
      '<strong>Best for: Scaling startups with content goals</strong>',
    ],
    cta: 'Try Growth',
    highlighted: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 1299,
    priceId: PRICE_ID.PRO,
    features: [
      'Unlimited outputs',
      'Weekly 1:1 coaching',
      'Custom automations and workflows',
      '<strong>Best for: Lean teams needing Full automation + strategy support</strong>',
    ],
    cta: 'Try Pro',
  },
];

export const Paywall: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);

  // Fetch user's current subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        return;
      }

      try {
        // Use Postgres function to get the most relevant subscription
        const { data, error } = await supabase
          .rpc('get_active_subscription', { p_user_id: user.id })
          .single();

        if (!error && data && typeof data === 'object' && 'plan' in data) {
          setCurrentPlan((data as any).plan);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
      }
    };

    fetchSubscription();
  }, [user]);

  const handleSubscribe = async (plan: PricingPlan) => {
    try {
      setLoadingPlan(plan.id);
      setError(null);

      // Get current session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        setError('Please log in to subscribe');
        setLoadingPlan(null);
        return;
      }

      // Call create-checkout edge function
      const { data, error: functionError } = await supabase.functions.invoke('create-checkout', {
        body: { priceId: plan.priceId },
      });

      if (functionError) {
        throw functionError;
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout');
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="overflow-x-hidden bg-transparent min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Heading */}
        <PageHeader
          title="Choose your plan to activate your workspace 🚀"
          subtitle="You're just one step away from unlocking your personalized AI Marketings Agents."
          showBackButton
          backButtonText={currentPlan ? 'Back to Dashboard' : 'Log out'}
          backButtonPath={currentPlan ? '/dashboard' : undefined}
          onBackButtonClick={!currentPlan ? () => signOut() : undefined}
          showAvatar={false}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center">
            {error}
          </div>
        )}

        {/* Pricing Cards Grid */}
        <div className="flex flex-col items-center md:flex-row md:justify-center gap-6 flex-wrap">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`w-[300px] h-[420px] flex flex-col justify-between bg-white rounded-[20px] p-5 shadow-lg ${
                plan.highlighted
                  ? 'border-2 border-purple-600 bg-white shadow-lg'
                  : 'border-slate-200'
              }`}
            >
              <div>
                <h3 className="text-xl font-bold text-center text-black mb-2">{plan.name}</h3>
                <div className="text-center mb-3">
                  <span className="text-black text-[40px] font-bold">${plan.price}</span>
                  <span className="text-black text-[18px] font-normal">/month</span>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-700">
                      <div className="h-[10px] min-w-[10px] rounded-full mt-[6px] bg-orange-500" />
                      <span
                        className="text-[16px] font-medium"
                        dangerouslySetInnerHTML={{ __html: feature }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={loadingPlan !== null || currentPlan === plan.id}
                className="w-full min-h-[36px] text-[18px] rounded-lg bg-gradient-to-r from-orange-500 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentPlan === plan.id
                  ? 'Current Plan'
                  : loadingPlan === plan.id
                  ? 'Loading...'
                  : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Stripe Security Text */}
        <p className="text-center text-sm text-text-secondary mt-8">
          Secure payment via Stripe - Cancel anytime
        </p>
      </div>
    </section>
  );
};
